import {Suspense, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Button, IconButton} from '@primer/react'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {ActivityEmpty} from './activity/ActivityEmpty'
import {ActivityError} from './activity/ActivityError'
import {ActionItemsLoading} from './action-items/ActionItemsLoading'
import {buildEventGroups} from '../../helpers/catch-up-helpers'
import {EventList} from './ActivityEventList'
import styles from './Activity.module.css'
import {Link} from '@github-ui/react-core/link'
import {useSuspenseActivityPageData} from './data/use-activity-page-data'
import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'

/** Activity section showing recent timeline updates since last engagement */
export function Activity() {
  return (
    <ErrorBoundary boundaryName="Activity" fallback={<ActivityError />}>
      <Suspense fallback={<ActionItemsLoading />}>
        <ActivityComponent />
      </Suspense>
    </ErrorBoundary>
  )
}

function ActivityComponent() {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())
  const {summary} = useSuspenseActivityPageData()
  const {
    urls: {changes},
  } = useLayoutRouteData()
  const items = buildEventGroups(summary, changes)

  const toggleGroup = (type: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }

  if (items.length === 0) {
    return <ActivityEmpty />
  }

  return (
    <div className={styles.activityContainer}>
      {items.map(item => {
        const isExpanded = expandedGroups.has(item.type)
        const linkText = item.linkText || `View ${item.label.toLowerCase()}`
        return (
          <div key={`catch-up-item-${item.type}`} className={styles.activityItem}>
            <div className={styles.activityHeader}>
              {item.count > 0 && (
                <IconButton
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${item.label.toLocaleLowerCase()}`}
                  size="small"
                  variant="invisible"
                  onClick={() => toggleGroup(item.type)}
                  icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                />
              )}
              <span className={styles.activityLabel}>{item.label}</span>
              {item.link && (
                <Button
                  as={Link}
                  to={item.link}
                  size="small"
                  variant="invisible"
                  aria-label={linkText}
                  className={styles.activityButton}
                >
                  {linkText}
                </Button>
              )}
            </div>
            {isExpanded && (
              <div className={styles.activityDetails}>
                <EventList events={item.events} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
